
import { Divider, Drawer, DrawerProps, Modal, Space } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import React, {
  FC,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useState,
} from 'react';

import CommonFormAnt, { CommonFormAntProps, CommonFormAntActions } from './Forms/CommonFormAnt';


import { isPromise } from './Forms/FormHelpers';
import classNames from 'classnames';
//import { useAuthorized } from './Auth/AuthHelpers';
import CommonButton from './Buttons/CommonButton';

export interface CommonOverlayComponentProps extends ModalProps, DrawerProps {
  type?: 'modal' | 'drawer';
  className?: string;
  title?: React.ReactNode;
  visible?: boolean;
  autoCloseOnSuccess?: boolean;
  confirmLoading?: boolean;
  onCancel?: () => any;
  onSubmit?: (any) => any;
  cleanPayload?: boolean;
  formsOpts?: CommonFormAntProps;
  hasButton?: any;
  destroyOnClose?: boolean;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  buttons?: any[];
  resetFormOpen?: boolean;
  ref?: any;
  children?: any;
  permission?: any;
  role?: string;
  loading?: boolean;
}
export interface refForm {
  current: { form: () => CommonFormAntActions };
}
const CommonOverlayComponent: FC<CommonOverlayComponentProps> = forwardRef((props, ref) => {
  const [visible, setVisible] = React.useState(props.visible);

  const [refForm, setRefForm] = React.useState();
  const submitFunc = useMemo(
    () => props.onSubmit || props?.formsOpts?.onSubmit || undefined,
    [props.onSubmit, props?.formsOpts?.onSubmit]
  );

  const [submitted, setSubmitted] = useState(false);
  const formRef: { current: CommonFormAntActions } = useRef(null);

  //const isAuthorized = useAuthorized({ permission: props.permission, role: props.role });
  const isAuthorized = true;

  useImperativeHandle(ref, () => ({
    setVisible,
    getForm: () => getForm(),
  }));

  useEffect(() => {
    setVisible(props.visible);
    resetForm();
  }, [props.visible, props.confirmLoading]);

  const closeModal = () => {
    if (props.onCancel) {
      props.onCancel();
    }
    setVisible(false);

    if (props.destroyOnClose) {
      Modal.destroyAll();
    }
    if (props.resetFormOpen) {
      resetForm();
    }
  };

  const getForm = () => {
    const FORM: CommonFormAntActions = formRef?.current || props?.formsOpts?.ref?.current;
    return FORM;
  };

  const resetForm = () => {
    if (formRef?.current && props?.formsOpts) {
      formRef?.current?.resetForm();
    }
  };

  const toggleModal = () => {
    setVisible(!visible);
  };

  const onSubmit = async () => {
    setSubmitted(true);

    const FORM = getForm();
    const values = (await FORM?.submit(props?.cleanPayload)) || undefined;

    if (!values?.errorFields && submitFunc) {
      setSubmitted(true);

      if (isPromise(submitFunc)) {
        submitFunc(values)
          .then((success) => {
            if (props.autoCloseOnSuccess) {
              closeModal();
            }
          })
          .catch(() => {
            setSubmitted(false);
          })
          .finally(() => {
            setSubmitted(false);
          });
      } else {

        submitFunc(values);
        setTimeout(() => {
          setSubmitted(false);
          if (props.autoCloseOnSuccess) {
            closeModal();
          }
        }, 1500);
      }
    } else if (values?.errorFields) {
      const firstFieldError = values?.errorFields?.[0]?.name;
      if (firstFieldError) FORM.form.scrollToField(firstFieldError, { behavior: 'smooth' });
      setTimeout(
        () => setSubmitted(false),

        1500
      );
    }
  };

  let overlayComp = null;

  const Buttons = React.useMemo(() => {
    const btns = [
      <CommonButton key="cancelBtn" danger={true} tooltip={props.cancelText} onClick={closeModal}>
        {props.cancelText}
      </CommonButton>,
    ];
    if (isAuthorized && submitFunc) {
      btns.push(
        <CommonButton
          type="primary"
          key="submitBtn"
          tooltip={props.okText}
          onClick={onSubmit}
          disabled={submitted}
          loading={submitted}
        >
          {props.okText}
        </CommonButton>
      );
    }

    if (props.buttons) {
      props.buttons.map((el) => btns.push(el));
    }

    return btns;
  }, [submitFunc, props.buttons, submitted]);

  const overlayClassName = classNames(
    'commonOverlay',
    props.type,
    props.className,
    { authorized: isAuthorized },
    { notAuthorized: !isAuthorized }
  );

  if (props.type === 'drawer') {
    overlayComp = (
      <Drawer
        title={props.title}
        placement={props.placement}
        className={overlayClassName}
        closable={true}
        mask={props.mask}
        maskClosable={props.maskClosable}
        forceRender={props.forceRender}
        onClose={closeModal}
        visible={visible}
        width={props.width || 500}
        destroyOnClose={props.destroyOnClose}
        getContainer={props.getContainer}
      >
        {visible && props.formsOpts && props.formsOpts.fields && (
          <CommonFormAnt
            key="drawerForm"
            ref={formRef}
            role={props.role}
            permission={props.permission}
            {...props.formsOpts}
          />
        )}
        {props.children}
        <div key="buttons" className="overlayFooter">
          <Divider />
          <Space className="overlayButtons" style={{ float: 'right' }}>{Buttons}</Space>
        </div>
      </Drawer>
    );
  } else {
    overlayComp = (
      <Modal
        title={props.title}
        visible={visible}
        width={props.width}
        className={overlayClassName}
        confirmLoading={props.confirmLoading}
        onCancel={closeModal}
        footer={Buttons}
        closable={true}
        maskClosable={true}
        getContainer={props.getContainer}
      >
        {props.formsOpts && props.formsOpts.fields && (
          <CommonFormAnt
            key="modalForm"
            ref={formRef}
            role={props.role}
            permission={props.permission}
            {...props.formsOpts}
          />
        )}
        {visible && props.children}
      </Modal>
    );
  }

  return (
    <>
      {overlayComp}

      {props.hasButton && (
        <CommonButton type="primary" onClick={toggleModal}>
          {props.hasButton}
        </CommonButton>
      )}
    </>
  );
});

CommonOverlayComponent.defaultProps = {
  visible: false,
  okText: 'Conferma',
  cancelText: 'Annulla',
  type: 'drawer',
  cleanPayload: true,
  hasButton: false,
  placement: 'right',
  title: '',
  permission: '*',
  role: undefined,
  loading: false,
  autoCloseOnSuccess: true,
};
export default CommonOverlayComponent;
